import { useEffect, useState } from "react";
import { LogoLights } from "./CometBorder";
import { VendorMark } from "./VendorMark";

export interface RotatingCompanyEntry {
  label: string;
  logoCompany: string;
}

interface RotatingCompanyProps {
  entries: readonly RotatingCompanyEntry[];
  /** The name the logo rules resolve against. */
  modelName: string;
  /** The title, which sits between the mark and the organization name. */
  title: string;
}

// Each organization holds still for HOLD_MS after its 1.2s cross-fade ends;
// the fade length matches `.upcoming-card__rotating-layer` in styles.css.
const FADE_MS = 1200;
const HOLD_MS = 2400;

/**
 * Shows one organization at a time for a forecast attributed to several, with
 * the logo and the name changing together. The outgoing pair fades out while
 * the incoming one fades in over it, and two lights circling the logo's frame
 * mark it as one that changes. Reduced motion keeps the cycle but drops the
 * fade and the lights.
 */
export function RotatingCompany({ entries, modelName, title }: RotatingCompanyProps) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (entries.length < 2) return;
    const timer = window.setInterval(() => setStep((current) => current + 1), FADE_MS + HOLD_MS);
    return () => window.clearInterval(timer);
  }, [entries.length]);

  const current = entries[step % entries.length];
  const previous = step > 0 ? entries[(step - 1) % entries.length] : null;
  const layers = [
    ...(previous ? [{ entry: previous, key: step - 1, state: "out" }] : []),
    { entry: current, key: step, state: "in" }
  ];

  return (
    <span className="model-card__heading upcoming-card__rotating">
      <span className="upcoming-card__rotating-stack">
        {layers.map(({ entry, key, state }) => (
          <span className={`upcoming-card__rotating-layer upcoming-card__rotating-layer--${state}`} key={key}>
            <VendorMark company={entry.logoCompany} modelName={modelName} />
          </span>
        ))}
        <span className="upcoming-card__logo-lights">
          <LogoLights />
        </span>
      </span>
      <span className="model-card__title-wrap">
        <strong>{title}</strong>
        <small className="upcoming-card__rotating-stack">
          {layers.map(({ entry, key, state }) => (
            <span className={`upcoming-card__rotating-layer upcoming-card__rotating-layer--${state}`} key={key}>
              {entry.label}
            </span>
          ))}
        </small>
      </span>
    </span>
  );
}
